import React, { useState, useEffect } from 'react';
import { Save, Wrench, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getMaintenanceSettings, updateMaintenanceSettings } from '@/services/settingsApi';

const toISOString = (date, time) => {
  if (!date) return null;
  const [hours, minutes] = (time || '00:00').split(':').map(Number);
  const d = new Date(date);
  d.setHours(hours || 0, minutes || 0, 0, 0);
  return d.toISOString();
};

export const MaintenanceTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceStartDate, setMaintenanceStartDate] = useState(null);
  const [maintenanceStartTime, setMaintenanceStartTime] = useState('00:00');
  const [maintenanceEndDate, setMaintenanceEndDate] = useState(null);
  const [maintenanceEndTime, setMaintenanceEndTime] = useState('23:59');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getMaintenanceSettings();
        const data = res?.data || res || {};
        setMaintenanceEnabled(!!data.maintenance_mode);
        setMaintenanceMessage(data.maintenance_message || '');
        if (data.maintenance_start) {
          const start = new Date(data.maintenance_start);
          setMaintenanceStartDate(start);
          setMaintenanceStartTime(format(start, 'HH:mm'));
        }
        if (data.maintenance_end) {
          const end = new Date(data.maintenance_end);
          setMaintenanceEndDate(end);
          setMaintenanceEndTime(format(end, 'HH:mm'));
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to load maintenance settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMaintenanceSettings({
        maintenance_mode: maintenanceEnabled,
        maintenance_message: maintenanceMessage,
        maintenance_start: toISOString(maintenanceStartDate, maintenanceStartTime),
        maintenance_end: toISOString(maintenanceEndDate, maintenanceEndTime),
      });
      toast.success('Maintenance settings saved');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Maintenance Mode
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div>
            <p className="font-medium text-slate-900">Enable Maintenance Mode</p>
            <p className="text-sm text-slate-600">Put the site into maintenance mode immediately</p>
          </div>
          <Switch checked={maintenanceEnabled} onCheckedChange={setMaintenanceEnabled} />
        </div>

        {maintenanceEnabled && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800 font-medium">
              ⚠️ Maintenance mode is currently active
            </p>
          </div>
        )}

        <div>
          <Label>Maintenance Message</Label>
          <Textarea
            value={maintenanceMessage}
            onChange={(e) => setMaintenanceMessage(e.target.value)}
            rows={4}
            className="mt-2"
          />
        </div>

        <div className="border-t pt-6 space-y-4">
          <p className="font-medium text-slate-900">Schedule (optional)</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date & Time</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal mt-2">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {maintenanceStartDate ? format(maintenanceStartDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={maintenanceStartDate}
                    onSelect={setMaintenanceStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                value={maintenanceStartTime}
                onChange={(e) => setMaintenanceStartTime(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>End Date & Time</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal mt-2">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {maintenanceEndDate ? format(maintenanceEndDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={maintenanceEndDate}
                    onSelect={setMaintenanceEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                value={maintenanceEndTime}
                onChange={(e) => setMaintenanceEndTime(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </CardContent>
    </Card>
  );
};